import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { useLocation, useNavigate } from "react-router-dom";
import { MdCheckCircle } from 'react-icons/md';
import reactLogo from "../assets/policy-logo.png";
import 'bootstrap/dist/css/bootstrap.min.css';
import "react-toastify/dist/ReactToastify.css";
import "../styles/Renewal.css";
import CONFIG from "../utils/config";

// Hardcoded data for add-ons since there is no API
const staticAddons = [
    { code: "GCC_COV", name: "GCC Coverage", cost: 50, coverageApplicableYN: "O" },
    { code: "REPL_CAR", name: "Replacement Car", cost: 1000, coverageApplicableYN: "O" },
    { code: "ROAD_ASSIST", name: "Roadside Assistance", cost: 75, coverageApplicableYN: "O" },
    { code: "PERSONAL_ACC", name: "Personal Accident", cost: 100, coverageApplicableYN: "O" },
    { code: "OFF_ROAD_COV", name: "Off-Road Coverage", cost: 150, coverageApplicableYN: "O" },
];

const RenewalOptions = () => {
    const [selectedCoverages, setSelectedCoverages] = useState([]);
    const [selectedAddons, setSelectedAddons] = useState([]);
    const [quotes, setQuotes] = useState([]);
    const [selectedPlanName, setSelectedPlanName] = useState(null);
    const [loading, setLoading] = useState(true);
    const [selectedPlanCode, setSelectedPlanCode] = useState(null); // Changed to store plan code
    const location = useLocation();
    const { premium, id, mobileNumber } = location.state || {};

    const navigate = useNavigate();

    useEffect(() => {
        const fetchPlans = async () => {
            setLoading(true);
            try {
                const response = await fetch(`${CONFIG.BASE}/product-plan/product`, {
                    method: "POST",
                    headers: {
                        "Accept": "application/json",
                        "Content-Type": "application/json"
                    },
                    body: JSON.stringify({ productCode: 105 })
                });

                if (!response.ok) {
                    throw new Error("Failed to fetch product plans");
                }

                const data = await response.json();
                const mappedData = data.map((item) => ({
                    id: item.planCode,
                    name: item.planNameShort,
                    description: item.planDescription,
                    premium: premium || 0,
                    coverages: item.coverages || [],
                    limit: item.planLimit1Description || "Not specified"
                }));

                setQuotes(mappedData);
                if (mappedData.length > 0) {
                    setSelectedPlanName(mappedData[0].name);
                    setSelectedPlanCode(mappedData[0].id); // Set the initial selected plan code
                }
            } catch (error) {
                console.error("API Error:", error);
                toast.error("Failed to load product plans.", {
                    position: "top-right",
                    autoClose: 2000,
                });
            } finally {
                setLoading(false);
            }
        };

        fetchPlans();
    }, [premium]);

    const handleOptionalCoverageChange = (coverageCode) => {
        setSelectedCoverages(prev =>
            prev.includes(coverageCode)
                ? prev.filter(item => item !== coverageCode)
                : [...prev, coverageCode]
        );
    };

    const handleAddOnChange = (addOnCode) => {
        setSelectedAddons(prev =>
            prev.includes(addOnCode)
                ? prev.filter(item => item !== addOnCode)
                : [...prev, addOnCode]
        );
    };
    
    const handlePlanSelection = (planName, planCode) => {
        setSelectedPlanName(planName);
        setSelectedPlanCode(planCode); // Correctly update the plan code
    };

    const handleNext = async () => {
        if (!selectedPlanCode) {
            toast.error("Please select a plan before proceeding!", {
                position: "top-right",
                autoClose: 2000,
            });
            return;
        }

        const selectedPlan = quotes.find(q => q.id === selectedPlanCode); // Find the plan using the selectedPlanCode
        if (!selectedPlan) {
            toast.error("Selected plan not found.", { position: "top-right", autoClose: 2000 });
            return;
        }

        let totalPremium = selectedPlan.premium || 0;

        const optionalCoverageCost = selectedCoverages.length * 50;
        totalPremium += optionalCoverageCost;

        const addonCost = selectedAddons.reduce((sum, addonCode) => {
            const addOnDetail = staticAddons.find(ao => ao.code === addonCode);
            return sum + (addOnDetail ? addOnDetail.cost : 0);
        }, 0);
        totalPremium += addonCost;

      
        const requestBody = {
            mobileNumber,
            selectedPlan: selectedPlanCode 
        };

       
        if (id) {
            if (id.length < 10) {
                requestBody.policyNumber = id;
            } else {
                requestBody.qid = id;
            }
        }

        try {
            const response = await fetch(`${CONFIG.BASE}/insurance/select-plan`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json"
                },
                body: JSON.stringify(requestBody)
            });

            if (!response.ok) {
                throw new Error("Failed to submit selected plan");
            }

            toast.success("Plan submitted successfully. Redirecting...", {
                position: "top-right",
                autoClose: 2000,
            });

            setTimeout(() => {
                const dataToPass = { premium: totalPremium };
                navigate("/payment", { state: dataToPass });
            }, 2000);
        } catch (error) {
            console.error("Submission Error:", error);
            toast.error("Error submitting selected plan.", {
                position: "top-right",
                autoClose: 2000,
            });
        }
    };

    const formatCurrency = (amount) => new Intl.NumberFormat().format(amount);

    const renderQuoteCard = (quote) => {
        const mandatoryCoverages = quote.coverages.filter(c => c.coverageApplicableYN === 'M');
        const optionalCoverages = quote.coverages.filter(c => c.coverageApplicableYN === 'O');

        const selectedOptionalCoverages = optionalCoverages.filter(c => selectedCoverages.includes(c.coverageCode));
        const selectedAddonsDetails = staticAddons.filter(ao => selectedAddons.includes(ao.code));

        const optionalCoverageCost = selectedOptionalCoverages.length * 50;
        const addonCost = selectedAddonsDetails.reduce((sum, ao) => sum + ao.cost, 0);
        const displayedTotal = (quote.premium || 0) + optionalCoverageCost + addonCost;
        return (
            <div
                key={quote.id}
                className={`col-lg-3 col-md-4 col-sm-12 mb-4 renewal-options__card-container ${selectedPlanCode === quote.id ? 'selected' : ''}`}
                onClick={() => handlePlanSelection(quote.name, quote.id)}
            >
                <div className="renewal-options__card h-100">
                    <div className="card-body">
                        <h5 className="card-title text-center">{quote.name}</h5>
                        <p className="text-center" style={{ fontSize: "0.9rem", fontStyle: "italic" }}>{quote.description}</p>
                        <div className="premium-details">
                            <p><strong>Base Premium:</strong> <span className="base-premium">{formatCurrency(quote.premium)}</span></p>
                            {optionalCoverageCost > 0 && <p><strong>Optional Coverages:</strong> <span className="add-ons">{formatCurrency(optionalCoverageCost)}</span></p>}
                            {addonCost > 0 && <p><strong>Extra Add-ons:</strong> <span className="add-ons">{formatCurrency(addonCost)}</span></p>}
                            <p><strong>Total Premium:</strong> <span className="total-premium">{formatCurrency(displayedTotal)}</span></p>
                            <p><strong>Limit:</strong> <span className="base-premium">{quote.limit}</span></p>
                        </div>
                        <hr />
                        <h6 className="text-center">Coverages & Features</h6>
                        <ul className="list-unstyled coverages">
                            {mandatoryCoverages.map((cov, idx) => (
                                <li key={`man-${idx}`}>
                                    <MdCheckCircle className="check-icon" /> {cov.coverageDescShort || cov.coverageCompareDesc || "Coverage"}
                                </li>
                            ))}
                            {selectedOptionalCoverages.map((cov, idx) => (
                                <li key={`opt-${idx}`}>
                                    <MdCheckCircle className="check-icon" /> {cov.coverageDescShort || cov.coverageCompareDesc || "Optional Coverage"}
                                </li>
                            ))}
                            {selectedAddonsDetails.map((addOn, idx) => (
                                <li key={`add-${idx}`}>
                                    <MdCheckCircle className="check-icon" /> {addOn.name}
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            </div>
        );
    };

    const renderOptionalCoverage = (coverage, index) => (
        <div
            key={index}
            className={`add-on-card ${selectedCoverages.includes(coverage.coverageCode) ? 'selected' : ''}`}
            onClick={() => handleOptionalCoverageChange(coverage.coverageCode)}
        >
            <div className="add-on-card-body">
                <h6>{coverage.coverageDescShort || "Optional Coverage"}</h6>
                <p className="card-text">+{formatCurrency(50)}</p>
            </div>
        </div>
    );

    const renderAddonCard = (addOn, index) => (
        <div
            key={index}
            className={`add-on-card ${selectedAddons.includes(addOn.code) ? 'selected' : ''}`}
            onClick={() => handleAddOnChange(addOn.code)}
        >
            <div className="add-on-card-body">
                <h6>{addOn.name}</h6>
                <p className="card-text">+{formatCurrency(addOn.cost)}</p>
            </div>
        </div>
    );

    return (
        <>
            <div className="container-fluid">
                <div className="row">
                    <a className="d-flex justify-content-center align-items-center mb-4 text-decoration-none" href="/">
                        <img className="me-2 react-logo" src={reactLogo} alt="QIC Logo" width="160" height="80" />
                    </a>
                    <h4 className="text-center fw-bold" style={{ color: "#c33764" }}>Optional Coverages & Extra Add-ons</h4>
                    
                    {/* Optional Coverages from the plan */}
                    <div className="row mb-4">
                        <h5 className="text-center">Optional Plan Coverages</h5>
                        <div className="d-flex flex-wrap justify-content-center gap-3">
                            {quotes.length > 0 && selectedPlanName && (
                                quotes.find(q => q.name === selectedPlanName)?.coverages
                                    .filter(c => c.coverageApplicableYN === 'O')
                                    .map(renderOptionalCoverage)
                            )}
                        </div>
                    </div>
                    
                    <hr />

                    {/* Static Add-ons */}
                    <div className="row">
                        <h5 className="text-center">Additional Add-ons</h5>
                        <div className="d-flex flex-wrap justify-content-center gap-3">
                            {staticAddons.map(renderAddonCard)}
                        </div>
                    </div>
                </div>

                <div className="renewal-options">
                    <div className="container-fluid mt-4">
                        <h3 className="text-center fw-semibold">Premium Plans</h3>
                        {loading ? (
                            <div className="d-flex justify-content-center align-items-center mt-4">
                                <div className="loading-spinner">
                                    <div className="spinner-border text-primary" role="status">
                                        <span className="visually-hidden">Loading...</span>
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <div className="row">{quotes.map(renderQuoteCard)}</div>
                        )}

                        <div className="d-flex justify-content-end gap-4 mt-4">
                            <button className="btn btn-secondary" onClick={() => navigate("/")}>
                              Cancel
                            </button>
                            <button className="btn btn-primary" onClick={handleNext}>Next</button>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default RenewalOptions;